<template>
  <bottom-sheet
    :visible="visible"
    :close-icon-right-align="true"
    max-width="max-w-[500px]"
    @close="emit('close')"
  >
    <div class="flex justify-center">
      <div
        class="relative -mt-4 flex w-full flex-col gap-4 rounded-lg bg-white px-4 pb-4"
      >
        <section class="flex items-center justify-between">
          <div class="flex flex-col items-start">
            <h3 class="text-left text-xl text-dark-gray">
              {{ modalTitle }}
            </h3>
            <p class="text-xs text-steel-700">
              <span class="text-red-500">*</span>
              = Required Fields
            </p>
          </div>
        </section>

        <div class="flex flex-col gap-3">
          <div
            ref="vehicleSearchRef"
            class="relative"
          >
            <p class="mb-1 ml-1 text-start font-semibold text-steel-800">
              Vehicle
              <span class="text-red-500">*</span>
            </p>
            <div
              class="relative flex h-10 w-full items-center rounded-lg border border-steel-300"
              :class="{
                'h-16': form.vehicle?.id,
                'h-10': !form.vehicle?.id,
                'cursor-pointer': !isEdit,
              }"
              @click="openVehicleDropdown"
            >
              <InputComponent
                v-if="!form.vehicle?.id"
                input-label="test"
                wrapper-class="flex bg-white w-full text-sm items-center h-10 gap-1.5 px-2"
                placeholder="Choose Vehicle"
                :default-input-value="form.vehicle_query"
                :value="form.vehicle_query"
                @input-change="handleVehicleSearchInput"
              />

              <div
                v-else
                class="flex w-full items-center justify-between px-4 py-2 pr-10"
                :class="{
                  'cursor-pointer': !isEdit,
                }"
              >
                <div class="flex items-start gap-2">
                  <img
                    class="mt-1 h-5 w-5"
                    src="@/assets/loads/green-wifi.svg"
                    alt="wifi"
                  />

                  <div class="text-left">
                    <div class="flex justify-between">
                      <div class="text-left">
                        <p class="text-sm text-steel-1100">
                          {{ form?.vehicle?.truck_number || form?.vehicle.id }}
                        </p>
                      </div>
                      <div
                        v-if="form?.vehicle?.license_plate_number"
                        class="ml-2 text-right"
                      >
                        <p class="text-xs text-steel-700">
                          Plate #: {{ form?.vehicle.license_plate_number }}
                          <span v-if="form?.vehicle?.license_plate_state">
                            ({{ form?.vehicle.license_plate_state }})
                          </span>
                        </p>
                      </div>
                    </div>
                    <p class="text-xs text-steel-700">
                      {{ form?.vehicle.vehicle?.vin }}
                    </p>
                  </div>
                </div>
              </div>

              <img
                v-if="form.vehicle?.id && !isEdit"
                class="absolute right-4 h-2.5 opacity-60 hover:opacity-40"
                src="@/assets/close-icon.svg"
                alt="close"
                @click="clearVehicle"
              />
            </div>

            <div
              v-if="showVehicleDropdown"
              class="absolute z-20 flex max-h-[200px] w-full flex-col overflow-scroll rounded-lg border border-steel-300 bg-white py-2"
              :class="{
                'top-[95px]': form.vehicle?.id,
                'top-[70px]': !form.vehicle?.id,
              }"
            >
              <div v-if="isCarrierVehiclesLoading">
                <img
                  class="loading m-auto my-4 h-8 w-8"
                  src="@/assets/loading-v2-yellow.svg"
                  alt="loading"
                />
              </div>
              <div v-else-if="carrier_vehicles?.length > 0">
                <div
                  v-for="vehicle in carrier_vehicles"
                  :key="vehicle.id"
                  class="flex cursor-pointer items-center justify-between px-4 py-2 hover:bg-steel-100"
                  @click="handleSelectVehicle(vehicle)"
                >
                  <div class="flex items-start gap-2">
                    <img
                      class="mt-1 h-5 w-5"
                      src="@/assets/loads/green-wifi.svg"
                      alt="wifi"
                    />

                    <div class="text-left">
                      <div class="flex justify-between">
                        <div class="text-left">
                          <p class="text-sm text-steel-1100">
                            {{ vehicle?.truck_number || vehicle.id }}
                          </p>
                        </div>
                        <div
                          v-if="vehicle?.license_plate_number"
                          class="ml-2 text-right"
                        >
                          <p class="text-xs text-steel-700">
                            Plate #: {{ vehicle.license_plate_number }}
                            <span v-if="vehicle?.license_plate_state">
                              ({{ vehicle.license_plate_state }})
                            </span>
                          </p>
                        </div>
                      </div>
                      <p class="text-xs text-steel-700">
                        {{ vehicle.vehicle?.vin }}
                      </p>
                    </div>
                  </div>
                </div>

                <InfiniteScroll @load-more="loadMoreDetails"></InfiniteScroll>

                <img
                  v-if="isCarrierVehiclesLoading"
                  class="loading m-auto"
                  src="@/assets/loading-v2-yellow.svg"
                  alt="loading spinner"
                />
              </div>

              <div
                v-else
                class="p-3"
              >
                <p class="text-steel-700">
                  {{
                    form.vehicle_query?.length > 0
                      ? 'No matching capacities.'
                      : 'There are no Capacities to post.'
                  }}
                </p>
              </div>
            </div>
          </div>

          <div>
            <p class="mb-1 ml-1 text-start font-semibold text-steel-800">
              Equipment
              <span class="text-red-500">*</span>
            </p>
            <MultiSelectDropdown
              v-model="form.equipment"
              :options="equipment_options"
              placeholder="Choose Equipment"
              :single-option="true"
              :is-searchable="true"
              :input-search="equipmentSearch"
              @input-change="handleEquipmentSearchInput"
            ></MultiSelectDropdown>
          </div>

          <div class="w-full">
            <div
              class="relative mb-1 ml-1 flex justify-between text-start text-xs font-semibold text-steel-800"
            >
              <p class="ml-1 w-9/12 md:w-10/12">
                Origin
                <span class="text-red-500">*</span>
              </p>

              <p class="ml-4 w-3/12 md:w-2/12">DH</p>
            </div>
            <div class="flex">
              <InputComponent
                input-label="origin"
                wrapper-class="capacity-location-input flex bg-white w-9/12 md:w-10/12 text-sm items-center h-10 gap-1.5 px-2 !rounded-r-none"
                placeholder="Origin"
                :default-input-value="form.origin"
                :value="form.origin"
                @input-change="handleOriginInput"
              />
              <div
                class="capacity-dropdown -mt-[1px] h-[42px] w-3/12 !rounded-l-none rounded-r-lg border border-steel-300 md:w-2/12"
              >
                <dropdown-select
                  :options="deadheadOptions"
                  :default="
                    form?.origin_deadhead_tolerance?.toString() || '150'
                  "
                  :value="form?.origin_deadhead_tolerance?.toString() || '150'"
                  :exclude-selected="true"
                  class="deadhead-dropdown !my-0 !h-10"
                  @input="handleSelectDeadheadOrigin"
                />
              </div>
            </div>

            <div
              v-if="form.origin?.length > 2 && isOriginAutocompleteLoading"
              class="absolute z-50 mt-1 max-h-60 w-[calc(100%-40px)] overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
            >
              <img
                class="loading m-auto my-4 h-12 w-12"
                src="@/assets/loading-v2-yellow.svg"
                alt="loading"
              />
            </div>
            <ul
              v-else-if="
                form.origin?.length > 2 &&
                origin?.autocompleteResults?.length > 0
              "
              class="absolute z-50 mt-1 max-h-60 w-[calc(100%-40px)] overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
              tabindex="-1"
              role="listbox"
              aria-labelledby="listbox-label"
              aria-activedescendant="listbox-option-3"
            >
              <li
                v-for="(item, index) in origin?.autocompleteResults"
                :key="`origin-dropdown-value-${index}`"
                class="group relative cursor-pointer select-none py-2 pl-8 pr-4 hover:bg-yellow-500 hover:text-white"
                role="option"
              >
                <span
                  class="flex h-6 items-center truncate font-medium"
                  @click="handleAutocompleteResult('origin', item)"
                >
                  {{ item?.description }}
                </span>
              </li>
            </ul>
          </div>

          <div class="w-full">
            <div
              class="relative mb-1 ml-1 flex justify-between text-start text-xs font-semibold text-steel-800"
            >
              <p class="ml-1 w-9/12 md:w-10/12">Destination</p>

              <p class="ml-4 w-3/12 md:w-2/12">DH</p>
            </div>
            <div class="flex">
              <InputComponent
                input-label="destination"
                wrapper-class="capacity-location-input flex w-9/12 md:w-10/12 bg-white text-sm items-center h-10 gap-1.5 px-2 !rounded-r-none"
                placeholder="Destination"
                :default-input-value="form.destination"
                :value="form.destination"
                @input-change="handleDestinationInput"
              />
              <div
                class="capacity-dropdown -mt-[1px] h-[42px] w-3/12 !rounded-l-none rounded-r-lg border border-steel-300 md:w-2/12"
              >
                <dropdown-select
                  :options="deadheadOptions"
                  :default="
                    form?.destination_deadhead_tolerance?.toString() || '150'
                  "
                  :value="
                    form?.destination_deadhead_tolerance?.toString() || '150'
                  "
                  :exclude-selected="true"
                  class="deadhead-dropdown !my-0"
                  @input="handleSelectDeadheadDestination"
                />
              </div>
            </div>

            <div
              v-if="
                form.destination?.length > 2 && isDestinationAutocompleteLoading
              "
              class="absolute z-50 mt-1 max-h-60 w-[calc(100%-40px)] overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
            >
              <img
                class="loading m-auto my-4 h-12 w-12"
                src="@/assets/loading-v2-yellow.svg"
                alt="loading"
              />
            </div>
            <ul
              v-else-if="
                form.destination?.length > 2 &&
                destination?.autocompleteResults?.length > 0
              "
              class="absolute z-50 mt-1 max-h-60 w-[calc(100%-40px)] overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
              tabindex="-1"
              role="listbox"
              aria-labelledby="listbox-label"
              aria-activedescendant="listbox-option-3"
            >
              <li
                v-for="(item, index) in destination?.autocompleteResults"
                :key="`destination-dropdown-value-${index}`"
                class="group relative cursor-pointer select-none py-2 pl-8 pr-4 hover:bg-yellow-500 hover:text-white"
                role="option"
              >
                <span
                  class="flex h-6 items-center truncate font-medium"
                  @click="handleAutocompleteResult('destination', item)"
                >
                  {{ item?.description }}
                </span>
              </li>
            </ul>
          </div>

          <div class="relative">
            <div class="flex gap-4">
              <p
                class="mb-1 ml-1 flex-1 text-start font-semibold text-steel-800"
              >
                Available Date
                <span class="text-red-500">*</span>
              </p>
            </div>

            <div class="flex w-full gap-4">
              <div class="relative w-full">
                <InputComponent
                  input-label="start_date"
                  wrapper-class="flex w-full text-sm items-center h-10 gap-1.5 px-2 cursor-auto capacity-date-wrapper"
                  placeholder=""
                  :default-input-value="form.start_date"
                  :value="form.start_date"
                  :show-clear-button="false"
                  :readonly="true"
                  @input-change="handleInputChange"
                  @input-click="toggleStartDatePicker"
                />

                <img
                  src="@/assets/calendar-light.svg"
                  class="absolute right-2 top-2 cursor-pointer opacity-90"
                  alt="calendar"
                  @click="toggleStartDatePicker"
                />
              </div>
            </div>

            <div
              v-show="showStartDatePicker"
              ref="startCalendarRef"
              class="absolute bottom-12 left-0 z-50 rounded-lg bg-white p-4 shadow-md"
            >
              <date-picker
                :initial-date="initialStartDate"
                :field="'start_date'"
                :handle-input="setSelectedStartDay"
                date-format="MM/DD/YYYY"
                :restrict-previous="true"
                @day-selected="setSelectedStartDay"
              ></date-picker>
            </div>
          </div>
        </div>

        <section
          class="mt-3 flex justify-end gap-2 [&>*]:flex-1 md:[&>*]:flex-initial"
        >
          <ButtonComponent
            class="min-w-[100px] border border-steel-300 md:w-min"
            @click="emit('close')"
          >
            Not Now
          </ButtonComponent>
          <ButtonComponent
            variant="primary"
            class="md:w-[150px]"
            :is-loading="isCreateTruckPostingLoading"
            :disabled="isButtonDisabled"
            @click="handleSubmit"
          >
            {{ modalTitle }}
          </ButtonComponent>
        </section>
      </div>
    </div>
  </bottom-sheet>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toast-notification';
import debounce from 'lodash.debounce';
import dayjs from 'dayjs';
import {
  BottomSheet,
  ButtonComponent,
  InputComponent,
  DatePicker,
  MultiSelectDropdown,
  InfiniteScroll,
  CapacityNotification,
  LoadDropdown as DropdownSelect,
} from '@/components';
import { useClickAway, createFilterQuery } from '@/utils';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  isEdit: {
    type: Boolean,
    default: false,
  },
  isReposting: {
    type: Boolean,
    default: false,
  },
  capacity: {
    type: Object,
  },
  isLoadBoardView: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const toast = useToast();
const router = useRouter();

const emit = defineEmits([
  'close',
  'error',
  'handle-tab-selection',
  'failed-capacity-notification',
]);

const currentDate = computed(() => {
  return dayjs().format('MM/DD/YYYY');
});

const form = ref({
  origin: '',
  destination: '',
  origin_area_id: '',
  destination_area_id: '',
  start_date: currentDate.value,
  end_date: '',
  origin_deadhead_tolerance: 150,
  destination_deadhead_tolerance: 150,
  origin_deadhead: '',
  destination_deadhead: '',
  equipment: [],
  vehicle: {},
  vehicle_query: '',
});

const is_reposting = ref(false);

const modalTitle = computed(() => {
  if (is_reposting.value) {
    return 'Repost Capacity';
  } else if (props.isEdit) {
    return 'Edit Capacity';
  } else {
    return 'Post Capacity';
  }
});

onMounted(() => {
  const capacityFromStorage = JSON.parse(
    localStorage.getItem('post-capacity') || 'null'
  );

  if (props?.isReposting) {
    is_reposting.value = true;
  }

  if (props?.capacity?.id) {
    const { capacity } = props;

    const startDate = dayjs(capacity?.start_date);
    const today = dayjs();
    const isBefore = startDate.isBefore(today);
    const repostingStartDate = isBefore
      ? dayjs(today).format('MM/DD/YYYY')
      : dayjs(capacity?.start_date).format('MM/DD/YYYY');

    form.value = {
      origin: capacity.origin_search_display_name,
      destination: capacity.destination_search_display_name,
      origin_area_id: capacity.origin_area_id,
      destination_area_id: capacity.destination_area_id,
      origin_deadhead_tolerance: capacity.origin_deadhead_tolerance || 150,
      destination_deadhead_tolerance:
        capacity.destination_deadhead_tolerance || 150,
      equipment: [capacity.posted_equipment_type],
      vehicle: capacity.carrier_vehicle,
      ...(props?.isReposting
        ? { start_date: repostingStartDate }
        : { start_date: capacity?.start_date }),
      end_date: capacity.end_date,
    };
  } else if (capacityFromStorage) {
    form.value = capacityFromStorage;

    if (capacityFromStorage?.isReposting) {
      is_reposting.value = true;
    }

    localStorage.removeItem('post-capacity');
  }
});

const showVehicleDropdown = ref(false);

const openVehicleDropdown = () => {
  if (!props.isEdit) {
    showVehicleDropdown.value = true;
  }
};
const toggleVehicleDropdown = () => {
  if (!props.isEdit) {
    showVehicleDropdown.value = !showVehicleDropdown.value;
  }
};

const handleSelectVehicle = (vehicle) => {
  form.value.vehicle = vehicle;
  toggleVehicleDropdown();
};

const isButtonDisabled = computed(
  () =>
    form.value?.equipment?.length === 0 ||
    !form.value.origin ||
    !form.value.vehicle?.id
);

const isCarrierVehiclesLoading = computed(
  () => store.state.loads?.is_carrier_vehicles_loading
);

const vehiclesPagination = computed(
  () => store.state.loads?.eld_carrier_vehicles_pagination
);

const carrier_vehicles = computed(
  () => store.state.loads?.eld_carrier_vehicles
);

const carrier_id = computed(
  () =>
    store.state.users?.currentUser?.requested_carrier_id ||
    store.state.users?.currentUser?.company?.carrier_id
);

const loadMoreDetails = async () => {
  if (!isCarrierVehiclesLoading.value) {
    console.log('Hit infinite scroll');

    if (
      vehiclesPagination?.value?.totalRecords !==
      carrier_vehicles?.value?.length
    ) {
      await store.dispatch('loads/getCarrierVehicles', {
        id: carrier_id.value,
        page: vehiclesPagination?.value?.currentPage + 1,
        refetch: true,
      });
    }
  }
};

const origin = computed(() => store.state.laneSearches.origin);
const destination = computed(() => store.state.laneSearches.destination);
const isOriginAutocompleteLoading = computed(
  () => store.state.laneSearches.isOriginAutocompleteLoading
);
const isDestinationAutocompleteLoading = computed(
  () => store.state.laneSearches.isDestinationAutocompleteLoading
);
const isCreateTruckPostingLoading = computed(
  () => store.state.loads.is_create_truck_posting_loading
);

const equipment_types = computed(() => {
  const raw = store.state.loads?.equipment_types || {};

  return Object.fromEntries(
    Object.entries(raw).filter(([key]) => key.toLowerCase() !== 'other')
  );
});

const equipmentSearch = ref('');

const handleEquipmentSearchInput = (e) => {
  if (e?.target?.value) {
    equipmentSearch.value = e.target.value.toLowerCase();
  } else {
    equipmentSearch.value = '';
  }
};

const equipment_options = computed(() => {
  return Object.entries(equipment_types?.value)
    ?.map(([value, key]) => {
      return { label: key, value };
    })
    .filter((option) => {
      return option.label.toLowerCase().includes(equipmentSearch.value);
    });
});

const handleInputChange = (value, field) => {
  form.value = { ...form.value, [field]: value.trim() };
};

const deadheadOptions = ref([
  '50',
  '100',
  '150',
  '200',
  '250',
  '300',
  '350',
  '400',
  '450',
  '500',
]);

const handleVehicleSearchInput = debounce(async (value) => {
  form.value.vehicle_query = value;
  showVehicleDropdown.value = true;

  await store.dispatch('loads/getCarrierVehicles', {
    id: carrier_id.value,
    query: `&q[without_active_truck_postings]=1&q[is_eld_active_eq]=1&q[s]=truck_number+asc&q[truck_number_or_license_plate_number_or_vehicle_vin_start]=${value}`,
  });
}, 500);

const clearVehicle = async () => {
  form.value.vehicle_query = '';
  form.value.vehicle = {};

  setTimeout(() => {
    showVehicleDropdown.value = false;
  }, 0);

  await store.dispatch('loads/getCarrierVehicles', {
    id: carrier_id.value,
    query: `&q[without_active_truck_postings]=1&q[is_eld_active_eq]=1&q[s]=truck_number+asc`,
  });
};

const handleSelectDeadheadOrigin = (item) => {
  form.value.origin_deadhead_tolerance = item;
};

const handleSelectDeadheadDestination = (item) => {
  form.value.destination_deadhead_tolerance = item;
};

const handleOriginInput = debounce(async (value) => {
  if (value.length > 2) {
    form.value.origin = value;
    await store.dispatch('laneSearches/getAutocomplete', {
      isType: 'origin',
      term: value,
    });
  }
}, 500);

const handleDestinationInput = debounce(async (value) => {
  if (value.length > 2) {
    form.value.destination = value;
    await store.dispatch('laneSearches/getAutocomplete', {
      isType: 'destination',
      term: value,
    });
  } else if (value.length === 0) {
    form.value.destination = '';
    form.value.destination_area_id = '';
    await store.dispatch('laneSearches/getAutocomplete', {
      isType: 'destination',
      term: value,
    });
  }
}, 500);

const handleAutocompleteResult = async (isType, item) => {
  await store.dispatch('laneSearches/updateFromGooglePlace', {
    isType: isType,
    googlePlace: item,
  });
  switch (isType) {
    case 'origin':
      store.commit('laneSearches/updateOrigin', {
        autocompleteResults: [],
        showBadge: true,
      });
      break;
    case 'destination':
      store.commit('laneSearches/updateDestination', {
        autocompleteResults: [],
        showBadge: true,
      });
      break;
  }
  const areaInformation = await store.dispatch(
    'laneSearches/updateAreaIdFromGooglePlace',
    isType
  );
  if (areaInformation?.data) {
    if (isType === 'origin') {
      form.value.origin_area_id = areaInformation?.data?.id;
      form.value.origin = item?.description;
    } else {
      form.value.destination_area_id = areaInformation?.data?.id;
      form.value.destination = item?.description;
    }
  }
};

const isLoading = ref(false);

const handlePostTruck = async () => {
  isLoading.value = true;

  const res = await store.dispatch('loads/createTruckPosting', {
    carrier_vehicle_id: form.value.vehicle?.id,
    origin_area_id: form.value.origin_area_id,
    destination_area_id: form.value.destination_area_id,
    start_date: dayjs(form.value.start_date).format('YYYY-MM-DD'),
    is_active: true,
    origin_deadhead_tolerance: Number(form.value.origin_deadhead_tolerance),
    destination_deadhead_tolerance: Number(
      form.value.destination_deadhead_tolerance
    ),
    posted_equipment_type: form.value.equipment[0],
    origin_search_display_name: form.value.origin,
    destination_search_display_name: form.value.destination,
  });

  if ([200, 201, 204].includes(res?.status) && res.data) {
    isLoading.value = false;

    toast.open({
      message: 'Capacity successfully posted.',
      position: 'top-right',
      duration: 5000,
    });

    const searchObject = {
      ...res?.data,
      origin: res?.data?.origin_search_display_name,
      destination: res?.data?.destination_search_display_name,
      selectedOrigin: res?.data?.origin_area_id,
      selectedDestination: res?.data?.destination_area_id,
      trailer: [res?.data?.posted_equipment_type],
      pickup_start: res?.data?.start_date,
      pickup_end: dayjs(res?.data?.start_date)
        .add(1, 'day')
        .format('YYYY-MM-DD'),
    };
    await store.commit('loads/setLoads', []);

    await store.commit('loads/setLoadSearchFilters', searchObject);

    const filterParams = createFilterQuery(
      {
        origin: res?.data?.origin_area?.name,
        destination: res?.data?.destination_area?.name,
        selectedOrigin: res?.data?.origin_area?.name,
        selectedDestination: res?.data?.destination_area?.name,
        trailer: [res?.data?.posted_equipment_type],
        pickup_start: res?.data?.start_date,
        pickup_end: dayjs(res?.data?.start_date)
          .add(1, 'day')
          .format('YYYY-MM-DD'),
      },
      {}
    );

    emit('handle-tab-selection', 'Active');
    store.dispatch('toast/addToast', {
      component: CapacityNotification,
      props: {
        visible: true,
        title: props.isLoadBoardView
          ? 'Viewing loads matching capacity for'
          : 'Capacity Successfully Posted',
        truck: res?.data,
        isLoadBoardView: props.isLoadBoardView,
        toggleModal: () => emit('close'),
      },
      ...(props.isLoadBoardView ? { manuallyClose: true } : {}),
    });

    if (props.isLoadBoardView) {
      emit('close', true);

      router.push({ name: 'Load Board', query: filterParams });
    } else {
      emit('close');
    }

    await store.dispatch('loads/getCarrierVehicles', {
      id: carrier_id.value,
      query:
        '&q[without_active_truck_postings]=1&q[is_eld_active_eq]=1&q[s]=truck_number+asc',
    });
  } else if ([422].includes(res?.status)) {
    isLoading.value = false;

    localStorage.setItem(
      'post-capacity',
      JSON.stringify({ ...form.value, isReposting: props.isReposting })
    );

    emit('close');

    emit('failed-capacity-notification');
  } else {
    // close with no specific error
    isLoading.value = false;

    emit('close');

    toast.open({
      message: 'Unexpected error occured',
      type: 'error',
      position: 'top-right',
      duration: 5000,
    });
  }
};

const handleUpdateTruck = async () => {
  isLoading.value = true;

  const res = await store.dispatch('loads/updateTruckPosting', {
    id: props.capacity?.id,
    carrier_vehicle_id: form.value.vehicle?.id,
    origin_area_id: form.value.origin_area_id,
    destination_area_id: form.value.destination_area_id,
    start_date: dayjs(form.value.start_date).format('YYYY-MM-DD'),
    posted_equipment_type: form.value.equipment[0],
    origin_deadhead_tolerance: form.value.origin_deadhead_tolerance,
    destination_deadhead_tolerance: form.value.destination_deadhead_tolerance,
    origin_search_display_name: form.value.origin,
    destination_search_display_name: form.value.destination,
  });

  if ([200, 201, 204].includes(res?.status) && res.data) {
    isLoading.value = false;

    toast.open({
      message: 'Capacity successfully updated.',
      position: 'top-right',
      duration: 5000,
    });
    emit('close');
  } else if ([422].includes(res?.status)) {
    // if out of range / error
    isLoading.value = false;

    emit('close');

    emit('failed-capacity-notification');
  } else {
    // close with no specific error
    isLoading.value = false;

    emit('close');

    toast.open({
      message: 'Unexpected error occured',
      type: 'error',
      position: 'top-right',
      duration: 5000,
    });
  }
};

const handleSubmit = () => {
  if (props.isEdit && !is_reposting.value) {
    handleUpdateTruck();
  } else {
    handlePostTruck();
  }
};

const showStartDatePicker = ref(false);

const toggleStartDatePicker = () => {
  showStartDatePicker.value = !showStartDatePicker.value;
};

const initialStartDate = computed(() => {
  return dayjs().format('MM/DD/YYYY');
});

const setSelectedStartDay = (fieldValue, dateSelected) => {
  if (dateSelected) {
    form.value.start_date = dayjs(dateSelected).format('MM/DD/YYYY');

    showStartDatePicker.value = false;
  }
};

const startCalendarRef = ref();
useClickAway(startCalendarRef, () => {
  showStartDatePicker.value = false;
});

const vehicleSearchRef = ref();
useClickAway(vehicleSearchRef, () => {
  showVehicleDropdown.value = false;
});
</script>

<style>
input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
}

.deadhead-dropdown > div:first-child {
  @apply flex h-10 items-center !rounded-l-none bg-steel-100 px-3 text-sm text-steel-800;
}

.deadhead-dropdown > div:nth-child(2) {
  @apply max-h-[150px] overflow-scroll md:max-h-max;
}

.equipment-dropdown > div:first-child {
  @apply !border-gray-300;
}

.capacity-date-wrapper > input {
  width: inherit;
}

.post-capacity-bottom-sheet .bottom-sheet-container {
  @apply overflow-y-visible;
}

.capacity-dropdown > div > div:first-child {
  border-top-right-radius: 7px;
  border-bottom-right-radius: 7px;
}

.dashboard-origin-dropdown > div > div:first-child {
  border-top-right-radius: 7px;
}
.dashboard-destination-dropdown > div > div:first-child {
  border-bottom-right-radius: 7px;
}
</style>
